import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuthState } from './AuthContext';
import { useGlobalWebSocket } from './GlobalWebSocketContext';
import { notificationService } from '@/lib/api/notification-service';
import { profileVisitService } from '@/lib/api/profile-visit-service';
import type { Notification } from '@/types/notification';
import { NotificationType } from '@/types/notification';
import type { ProfileVisit } from '@/types/profile-visit';
import { showNotificationToast, showMobileNotificationToast } from '@/components/notifications/NotificationToast';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { subscribeToPushNotifications, getPushSubscription } from '@/lib/service-worker';

// Separate interface for counts only (for badges - changes frequently)
interface NotificationCountContextType {
  unreadCount: number;
  unseenVisitsCount: number;
}

// Separate interface for notification list (for notification panel)
interface NotificationListContextType {
  notifications: Notification[];
  profileVisits: ProfileVisit[];
  isLoading: boolean;
}

// Separate interface for actions (never changes)
interface NotificationActionsContextType {
  loadNotifications: () => Promise<void>;
  markAsRead: (notificationIds: number[]) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: number) => Promise<void>;
  loadProfileVisits: () => Promise<void>;
  markVisitsAsSeen: (visitIds: number[]) => Promise<void>;
  requestPushPermission: () => Promise<void>;
  isPushEnabled: boolean;
}

// Combined interface for backwards compatibility
interface NotificationContextType extends NotificationCountContextType, NotificationListContextType, NotificationActionsContextType {}

// Separate contexts
const NotificationCountContext = createContext<NotificationCountContextType | null>(null);
const NotificationListContext = createContext<NotificationListContextType | null>(null);
const NotificationActionsContext = createContext<NotificationActionsContextType | null>(null);

// Hook for counts only - use this for badges (minimal re-renders)
export const useNotificationCount = (): NotificationCountContextType => {
  const context = useContext(NotificationCountContext);
  if (!context) {
    throw new Error('useNotificationCount must be used within NotificationProvider');
  }
  return context;
};

// Hook for notification list - use this for notification panel
export const useNotificationList = (): NotificationListContextType => {
  const context = useContext(NotificationListContext);
  if (!context) {
    throw new Error('useNotificationList must be used within NotificationProvider');
  }
  return context;
};

// Hook for actions only - use this when you only need methods
export const useNotificationActions = (): NotificationActionsContextType => {
  const context = useContext(NotificationActionsContext);
  if (!context) {
    throw new Error('useNotificationActions must be used within NotificationProvider');
  }
  return context;
};

// Combined hook for backwards compatibility
export const useNotifications = (): NotificationContextType => {
  const count = useNotificationCount();
  const list = useNotificationList();
  const actions = useNotificationActions();
  return useMemo(() => ({ ...count, ...list, ...actions }), [count, list, actions]);
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuthState();
  const { subscribe, isSessionJoined } = useGlobalWebSocket();
  const isMobile = useMediaQuery('(max-width: 768px)');
  
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [profileVisits, setProfileVisits] = useState<ProfileVisit[]>([]);
  const [unseenVisitsCount, setUnseenVisitsCount] = useState(0);
  const [isPushEnabled, setIsPushEnabled] = useState(false);

  // Load notifications
  const loadNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      setIsLoading(true);
      const data = await notificationService.getNotifications();
      setNotifications(data.notifications);
      setUnreadCount(data.unread_count);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Load profile visits
  const loadProfileVisits = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const visits = await profileVisitService.getProfileVisits({ include_seen: false });
      setProfileVisits(visits);
      setUnseenVisitsCount(visits.length);
    } catch (error) {
      console.error('Failed to load profile visits:', error);
    }
  }, [isAuthenticated]);

  // Mark notifications as read
  // IMPORTANT: Keep this callback stable by using functional state updates only
  // This prevents the WebSocket effect from re-running on every notification change
  const markAsRead = useCallback(async (notificationIds: number[]) => {
    try {
      const realIds = notificationIds.filter(id => id > 0);
      if (realIds.length > 0) {
        await notificationService.markAsRead(realIds);
      }

      // Use functional updates to avoid dependency on notifications state
      setNotifications(prev => {
        const idsSet = new Set(notificationIds);
        let unreadDecrement = 0;
        
        const updated = prev.map(n => {
          if (idsSet.has(n.id)) {
            if (!n.is_read) unreadDecrement++;
            return { ...n, is_read: true, read_at: new Date().toISOString() };
          }
          return n;
        });
        
        // Schedule unread count update after this render
        if (unreadDecrement > 0) {
          setTimeout(() => {
            setUnreadCount(c => Math.max(0, c - unreadDecrement));
          }, 0);
        }
        
        return updated;
      });
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
    }
  }, []); // No dependencies - fully stable callback

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead();
      
      setNotifications(prev => 
        prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
      );
      
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  }, []);

  // Delete notification
  // IMPORTANT: Keep this callback stable by using functional state updates only
  const deleteNotification = useCallback(async (notificationId: number) => {
    try {
      if (notificationId > 0) {
        await notificationService.deleteNotification(notificationId);
      }

      // Use functional update to check if notification was unread
      setNotifications(prev => {
        const notification = prev.find(n => n.id === notificationId);
        const wasUnread = notification && !notification.is_read;
        
        // Schedule unread count update if needed
        if (wasUnread) {
          setTimeout(() => {
            setUnreadCount(c => Math.max(0, c - 1));
          }, 0);
        }
        
        return prev.filter(n => n.id !== notificationId);
      });
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  }, []); // No dependencies - fully stable callback

  // Mark profile visits as seen
  const markVisitsAsSeen = useCallback(async (visitIds: number[]) => {
    try {
      await profileVisitService.markAsSeen(visitIds);
      
      setProfileVisits(prev => prev.filter(v => !visitIds.includes(v.id)));
      setUnseenVisitsCount(prev => Math.max(0, prev - visitIds.length));
    } catch (error) {
      console.error('Failed to mark visits as seen:', error);
    }
  }, []);

  // Request push notification permission and subscribe
  const requestPushPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.warn('Notifications API not supported in this browser');
      return;
    }
    
    const showToast = isMobile ? showMobileNotificationToast : showNotificationToast;
    
    // Bereits blockiert? User muss manuell in Browser-Einstellungen aendern
    if (Notification.permission === 'denied') {
      showToast({
        type: NotificationType.SYSTEM,
        title: 'Benachrichtigungen blockiert',
        content: 'Bitte aktiviere Benachrichtigungen in deinen Browser-Einstellungen',
      });
      return;
    }
    
    try {
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        setIsPushEnabled(true);
        
        // Web Push Subscription beim Server registrieren
        try {
          const subscription = await subscribeToPushNotifications();
          
          if (subscription) {
            showToast({
              type: NotificationType.SYSTEM,
              title: 'Push-Benachrichtigungen aktiviert ✓',
              content: 'Du erhältst jetzt Benachrichtigungen auch wenn der Browser geschlossen ist',
            });
          } else {
            // Fallback: Nur In-App Notifications
            showToast({
              type: NotificationType.SYSTEM,
              title: 'Benachrichtigungen aktiviert',
              content: 'Du erhältst Browser-Benachrichtigungen wenn die App geöffnet ist',
            });
          }
        } catch (subError) {
          console.warn('Push subscription failed, falling back to in-app only:', subError);
          showToast({
            type: NotificationType.SYSTEM,
            title: 'Benachrichtigungen aktiviert',
            content: 'Du erhältst Browser-Benachrichtigungen wenn die App geöffnet ist',
          });
        }
        
        // Test-Notification um zu zeigen dass es funktioniert
        setTimeout(() => {
          try {
            const testNotification = new Notification('Test erfolgreich! 🎉', {
              body: 'Browser-Benachrichtigungen funktionieren.',
              icon: ((window as any).__APP_FAVICON__ || '/favicon.svg'),
              tag: 'test-notification',
            });
            setTimeout(() => testNotification.close(), 4000);
          } catch (e) {
            console.warn('Test notification failed:', e);
          }
        }, 500);
        
      } else if (permission === 'denied') {
        setIsPushEnabled(false);
        showToast({
          type: NotificationType.SYSTEM,
          title: 'Benachrichtigungen abgelehnt',
          content: 'Du kannst dies später in den Browser-Einstellungen ändern',
        });
      }
    } catch (error) {
      console.error('Failed to request notification permission:', error);
    }
  }, [isMobile]);

  // Check push permission status and sync subscription
  useEffect(() => {
    if ('Notification' in window) {
      const isGranted = Notification.permission === 'granted';
      setIsPushEnabled(isGranted);
      
      // Wenn Permission erteilt und User eingeloggt, Subscription synchronisieren
      if (isGranted && isAuthenticated) {
        void (async () => {
          try {
            // Prüfe ob bereits subscribed
            const existingSubscription = await getPushSubscription();
            if (!existingSubscription) {
              // Automatisch subscriben wenn Permission schon erteilt
              await subscribeToPushNotifications();
            }
          } catch (e) {
            // Still ignore - not critical
          }
        })();
      }
    }
  }, [isAuthenticated]);

  // Load initial data
  useEffect(() => {
    if (isAuthenticated) {
      void loadNotifications();
      void loadProfileVisits();
    }
  }, [isAuthenticated, loadNotifications, loadProfileVisits]);

  // WebSocket listener for real-time notifications
  useEffect(() => {
    if (!isAuthenticated || !user || !subscribe) return;

    const handleWebSocketMessage = (data: any) => {
      try {
        if (data.type === 'notification') {
          const notification = data.notification as Notification;

          // Add to notifications list (prevent duplicates) and track if new
          setNotifications(prev => {
            if (prev.some(n => n.id === notification.id)) {
              return prev; // Already exists, skip duplicate
            }
            // Schedule count update and toast only for new notifications
            setTimeout(() => {
              setUnreadCount(c => c + 1);

              // Show toast notification
              const showToast = isMobile ? showMobileNotificationToast : showNotificationToast;
              showToast({
                type: notification.type,
                title: notification.title,
                content: notification.content,
                data: notification.data,
                characterImage: notification.data?.character_image,
                notificationId: notification.id
              });

              // Show browser notification if enabled
              if (isPushEnabled && 'Notification' in window) {
                const browserNotification = new Notification(notification.title, {
                  body: notification.content,
                  icon: ((window as any).__APP_FAVICON__ || '/favicon.svg'),
                  badge: ((window as any).__APP_FAVICON__ || '/favicon.svg'),
                });
                browserNotification.onclick = async () => {
                  try {
                    await markAsRead([notification.id]);
                  } catch (e) {
                    // ignore
                  }
                  window.focus();
                  try {
                    if (notification.type === NotificationType.PROFILE_VISIT) {
                      const isCreator = Boolean(notification.data?.is_creator);
                      const creatorId = notification.data?.creator_id;
                      const characterId = notification.data?.character_id;

                      if (isCreator && creatorId) {
                        window.location.href = `/creator/${creatorId}`;
                      } else if (characterId) {
                        window.location.href = `/characters/${characterId}`;
                      } else {
                        window.location.href = '/profile/visits';
                      }
                    } else if (notification.data) {
                      if (notification.data.session_id && notification.data.character_id) {
                        window.location.href = `/chats/${notification.data.character_id}?session=${notification.data.session_id}`;
                      } else if (notification.data.character_id) {
                        window.location.href = `/characters/${notification.data.character_id}`;
                      } else if (notification.data.match_id) {
                        window.location.href = '/matches';
                      } else if (notification.type === NotificationType.COIN_PURCHASE ||
                                 notification.type === NotificationType.SUBSCRIPTION) {
                        window.location.href = '/wallet';
                      } else {
                        window.location.href = '/notifications';
                      }
                    } else {
                      window.location.href = '/notifications';
                    }
                  } catch (e) {
                    window.location.href = '/notifications';
                  }
                };
              }
            }, 0);
            return [notification, ...prev];
          });
        } else if (data.type === 'notification_count_update') {
          setUnreadCount(data.unread_count);
        } else if (data.type === 'unread_count_update') {
          // Fallback: Bei session-spezifischer Änderung (Chat gelesen) den globalen Zähler frisch laden
          void (async () => {
            try {
              const { unread_count } = await notificationService.getUnreadCount();
              setUnreadCount(unread_count);
            } catch (e) {
              // ignore fetch error; UI bleibt beim letzten bekannten Stand
            }
          })();
        } else if ((data.type === 'message' || data.type === 'chat_message') && data.is_from_character && data.session_id) {
          // Smart Chat Suppression: Use isSessionJoined from GlobalWebSocketContext as Single Source of Truth
          // This is more reliable than URL-based checks which can have race conditions
          const isUserViewingSession =
            typeof isSessionJoined === 'function' &&
            isSessionJoined(String(data.session_id)) &&
            document.visibilityState === 'visible';

          // If user has joined this session and tab is visible, suppress notification
          // The user will see the message directly in the chat
          if (isUserViewingSession) {
            // Don't show toast or add to notification list - user sees it in real-time
            return;
          }

          // NOTE: We do NOT show a toast here anymore!
          // The backend sends the notification separately via type: 'notification'
          // which already shows the toast. Showing one here would cause DUPLICATE toasts.
          // The notification handler (above) handles toast display.
        } else if (data.type === 'profile_visit') {
          // Handle profile visit - only update ProfileVisits list
          // The actual notification arrives separately as type: 'notification' from the backend
          const visitData = data.data;

          // Add to profile visits list only
          const newVisit: ProfileVisit = {
            id: visitData.visit_id,
            visitor_id: 0,
            visited_user_id: user?.id || 0,
            character_id: visitData.character_id,
            visit_duration: visitData.visit_duration,
            visit_source: visitData.visit_source,
            triggered_message: false,
            visit_score: 0,
            created_at: visitData.timestamp,
            character: {
              id: visitData.character_id,
              name: visitData.character_name,
              profile_image: visitData.character_image,
            }
          };

          setProfileVisits(prev => [newVisit, ...prev]);
          setUnseenVisitsCount(prev => prev + 1);

          // NOTE: We do NOT show a toast here anymore!
          // The backend sends the notification separately via type: 'notification'
          // which already shows the toast. This prevents DUPLICATE toasts.
        }
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    // Subscribe to WebSocket events using GlobalWebSocketContext
    const unsubscribe = subscribe(handleWebSocketMessage);

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [isAuthenticated, user, isPushEnabled, isMobile, subscribe, isSessionJoined, markAsRead]);

  // Memoize count value - only for badges (changes when count changes)
  const countValue = useMemo<NotificationCountContextType>(() => ({
    unreadCount,
    unseenVisitsCount,
  }), [unreadCount, unseenVisitsCount]);

  // Memoize list value - for notification panel (changes when list changes)
  const listValue = useMemo<NotificationListContextType>(() => ({
    notifications,
    profileVisits,
    isLoading,
  }), [notifications, profileVisits, isLoading]);

  // Memoize actions value - stable references via useCallback
  const actionsValue = useMemo<NotificationActionsContextType>(() => ({
    loadNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    loadProfileVisits,
    markVisitsAsSeen,
    requestPushPermission,
    isPushEnabled,
  }), [loadNotifications, markAsRead, markAllAsRead, deleteNotification, loadProfileVisits, markVisitsAsSeen, requestPushPermission, isPushEnabled]);

  return (
    <NotificationCountContext.Provider value={countValue}>
      <NotificationListContext.Provider value={listValue}>
        <NotificationActionsContext.Provider value={actionsValue}>
          {children}
        </NotificationActionsContext.Provider>
      </NotificationListContext.Provider>
    </NotificationCountContext.Provider>
  );
};